import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from 'components/utils/DataTable';
import ChargeServices from 'services/ChargeServices';
import styles from './styles.module.css';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import ChargeGroupCreateDialog from 'components/popups/ChargeGroupCreateDialog';
import ChargeCreateDialog from 'components/popups/ChargeCreateDialog';
import ChargeGroupEditDialog from 'components/popups/ChargeGroupEditDialog';
import ChargeEditDialog from 'components/popups/ChargeEditDialog';
import Info from 'components/utils/Alert/Info';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AlertMessagesContext } from 'react-alert-messages';
import { useTranslation } from 'react-i18next';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';

export default function VariantConfig() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [isLoading, setLoading] = useState(false);
  const [charges, setCharges] = useState([]);
  const [chargeGroups, setChargeGroups] = useState([]);

  const [createChargeDialogOpen, setCreateChargeDialogOpen] = useState(false);
  const [createChargeGroupDialogOpen, setCreateChargeGroupDialogOpen] = useState(false);

  const [editCharge, setEditCharge] = useState(null);
  const [editChargeGroup, setEditChargeGroup] = useState(null);

  const [chargeToDelete, setChargeToDelete] = useState(null);
  const [chargeGroupToDelete, setChargeGroupToDelete] = useState(null);

  const [expandedChargeGroupId, setExpandedChargeGroupId] = useState(false);
  const [initialChargeGroupId, setInitialChargeGroupId] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const userHasSalesPersonWritePermission = user?.shop_permissions?.sales_persons_permission === USER_PERMISSIONS.WRITE;

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [fetchedCharges, fetchedChargeGroups] = await Promise.all([
          ChargeServices.getCharges(),
          ChargeServices.getChargeGroups(),
        ]);
        setCharges(fetchedCharges || []);
        setChargeGroups(fetchedChargeGroups || []);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoading(false);
    };
    fetchInitialData();
  }, [postAlertMessage]);

  const handleChargeCreateSuccess = async (newCharge) => {
    setCharges((prev) => [...prev, newCharge]);
    setCreateChargeDialogOpen(false);
  };

  const handleChargeUpdateSuccess = (updatedCharge) => {
    setCharges((prev) => prev.map((c) => (c.id === updatedCharge.id ? updatedCharge : c)));
    setEditCharge(null);
  };

  const handleChargeDeleteSuccess = async () => {
    setLoading(true);
    try {
      await ChargeServices.deleteCharge(chargeToDelete.id);
      setCharges((prev) => prev.filter((c) => c.id !== chargeToDelete.id));
      setChargeToDelete(null);
      postAlertMessage({ text: tManagement('Charge deleted successfully'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const handleChargeGroupCreateSuccess = async (newCharge) => {
    setChargeGroups((prev) => [...prev, newCharge]);
    setCreateChargeGroupDialogOpen(false);
  };

  const handleChargeGroupUpdateSuccess = (updatedChargeGroup) => {
    setChargeGroups((prev) => prev.map((c) => (c.id === updatedChargeGroup.id ? updatedChargeGroup : c)));
    setEditChargeGroup(null);
  };

  const handleChargeGroupDeleteSuccess = async () => {
    setLoading(true);
    try {
      await ChargeServices.deleteChargeGroup(chargeGroupToDelete.id);
      setChargeGroups((prev) => prev.filter((cg) => cg.id !== chargeGroupToDelete.id));
      setChargeGroupToDelete(null);
      postAlertMessage({ text: tManagement('Charge group deleted successfully'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const handleAccordionChange = (chargeGroupId) => (event, isExpanded) => {
    setExpandedChargeGroupId(isExpanded ? chargeGroupId : false);
  };

  const chargeHeaderData = [
    { label: tCommon('Charge'), id: 'name', type: 'text' },
    { label: tCommon('Category'), id: 'category', type: 'text' },
    { label: tCommon('Order'), id: 'order', type: 'text' },
    { label: tCommon('Value'), id: 'value', type: 'text' },
    { label: tCommon('Calculation Type'), id: 'calculation_type', type: 'text' },
    { label: tCommon('Apply On'), id: 'apply_on', type: 'text' },
    { label: tCommon('Min Amount'), id: 'min_amount', type: 'text' },
    { label: tCommon('Max Amount'), id: 'max_amount', type: 'text' },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => (
        <Grid className={styles.btnGroup}>
          {!item.group?.is_global && (
            <>
              <Tooltip title={tCommon('Edit Charge')}>
                <EditIcon className={styles.iconButton} onClick={() => setEditCharge(item)} />
              </Tooltip>
              <Tooltip title={tCommon('Delete Charge')}>
                <DeleteIcon className={styles.iconButton} onClick={() => setChargeToDelete(item)} />
              </Tooltip>
            </>
          )}
        </Grid>
      ),
    },
  ];

  return (
    <div className={styles.contentLayout}>
    {isLoading && <CircularProgress style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 1000 }} />}
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Manage Charges & Discounts')}</span>
          <div className={styles.filerInputSec}>
            {subscriptionType &&
              subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
              userHasSalesPersonWritePermission && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ backgroundColor: '#ff851b' }}
                  className={styles.actionBtn}
                  onClick={() => setCreateChargeGroupDialogOpen(true)}
                >
                  <NoteAddIcon className={styles.actionBtnIcon} />
                  {tCommon('New Charge Group')}
                </Button>
              )}
          </div>
        </div>
        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM ? (
          <RestrictionInfo
            title={tManagement('Discount & Charges feature not available')}
            content={tManagement('To get Discount & Charges support options, contact support team.')}
          />
        ) : (
          <div>
            {chargeGroups.length === 0 ? (
              <Info title={tManagement('No Charge Groups')} content={tManagement('Please create a new charge group.')} />
            ) : (
              <>
                <Box className={styles.tableHead}>
                  <Box sx={{ width: 40 }} />
                  <Typography className={styles.headerName}>{tCommon('Charge group')}</Typography>
                  <Typography className={styles.headerAction}>{tCommon('Actions')}</Typography>
                </Box>

                {chargeGroups.map((group) => {
                  const chargesForGroups = charges.filter((c) => c.group?.id === group.id);
                  return (
                    <Accordion
                      key={group.id}
                      expanded={expandedChargeGroupId === group.id}
                      onChange={handleAccordionChange(group.id)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          flexDirection: 'row-reverse',
                          width: '100%',
                          '& .MuiAccordionSummary-content': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          },
                        }}
                        aria-controls={`panel-${group.id}-content`}
                        id={`panel-${group.id}-header`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Typography className={styles.variantName} sx={{ minWidth: 150 }}>
                            {group.name}
                          </Typography>
                          {group.is_global ? (
                            <Chip
                              label={tCommon('default')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              style={{ marginLeft: '10px', height: '20px', fontSize: '10px' }}
                            />
                          ) : (
                            <Chip
                              label={tCommon('customs')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              style={{ marginLeft: '10px', height: '20px', fontSize: '10px' }}
                            />
                          )}
                        </div>
                        <div>
                          {!group.is_global && (
                            <>
                              <Tooltip title={tCommon('Edit Charge Group')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditChargeGroup(group);
                                  }}
                                  style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={tCommon('Delete Charge Group')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setChargeGroupToDelete(group);
                                  }}
                                  style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={tCommon('Add Charge')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setInitialChargeGroupId(group.id);
                                    setCreateChargeDialogOpen(true);
                                  }}
                                  style={{ paddingRight: '16px', color: 'rgb(128, 128, 128)' }}
                                >
                                  <AddCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        {chargesForGroups.length ? (
                          <div className={styles.myDataTable}>
                            <DataTable rows={chargesForGroups} columns={chargeHeaderData} />
                          </div>
                        ) : (
                          !isLoading && (
                            <Info title={tManagement('No Charges')} content={tManagement('No charges under this group. Create one.')} />
                          )
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {createChargeDialogOpen && (
        <ChargeCreateDialog
          groupId={initialChargeGroupId}
          handleClose={() => {
            setCreateChargeDialogOpen(false);
            setInitialChargeGroupId(null);
          }}
          handleSuccess={handleChargeCreateSuccess}
        />
      )}

      {editCharge && (
        <ChargeEditDialog
          chargeGroups={chargeGroups}
          charge={editCharge}
          handleClose={() => setEditCharge(null)}
          handleSuccess={handleChargeUpdateSuccess}
        />
      )}

      {createChargeGroupDialogOpen && (
        <ChargeGroupCreateDialog
          handleClose={() => setCreateChargeGroupDialogOpen(false)}
          handleSuccess={handleChargeGroupCreateSuccess}
        />
      )}

      {editChargeGroup && (
        <ChargeGroupEditDialog
          chargeGroup={editChargeGroup}
          handleClose={() => setEditChargeGroup(null)}
          handleSuccess={handleChargeGroupUpdateSuccess}
        />
      )}

      <ConfirmPopup
        data={!!chargeToDelete}
        handleClose={() => setChargeToDelete(null)}
        handleOkay={handleChargeDeleteSuccess}
        alertTitle={tManagement('Delete Charge?')}
        alertContent={tManagement('Are you sure you want to delete charge "{{name}}"?', { name: chargeToDelete?.name })}
        successBtnName={tCommon('Yes, delete')}
        isLoading={isLoading}
      />

      <ConfirmPopup
        data={!!chargeGroupToDelete}
        handleClose={() => setChargeGroupToDelete(null)}
        handleOkay={handleChargeGroupDeleteSuccess}
        alertTitle={tManagement('Delete Charge Group?')}
        alertContent={tManagement('Are you sure you want to delete charge group "{{name}}"?', { name: chargeGroupToDelete?.name })}
        successBtnName={tCommon('Yes, delete')}
        isLoading={isLoading}
      />
    </div>
  );
}
